<script setup lang="ts">
import { useBillingRequests } from '@/composables/billing/useBillingRequests';
import { useBillingProducts } from '@/composables/billing/useBillingProducts';
import { useBillingCheckoutOptions } from '@/composables/billing/billing.queries';
import { computed, ref, toRefs, watch } from 'vue';
import { Components } from '@/api/generated/api';
import {
  computeProductPriceBreakdownCents,
  constrainSeatQuantity,
  formatAmount,
  getDiscountPercentForCheckoutSource,
  getMinStudentQuantity,
  getMinTeamQuantity,
  getProductsMarketCurrency,
  hasMonthlyBilling,
  isProductAvailableForPurchase,
  MAX_SEAT_QUANTITY,
  MIN_STUDENT_QUANTITY,
  MIN_TEAM_QUANTITY,
  purchasableProductOrder,
  STUDENT_QUANTITY_STEP,
  TEAM_QUANTITY_STEP,
  toMonthlyEquivalentCents,
  type BillingInterval,
  type CheckoutPriceDisplay,
  type ProductWithMeta,
  yearlySavingsPercent,
} from '@/utils/billing';
import BillingProductCheckoutCard from '../billing-product-price-calculator/BillingProductCheckoutCard.vue';
import NumberStepper from '@/core/elements/NumberStepper.vue';
import { useBillingState } from '@/composables/billing/useBillingState';
import CustomButton from '@/core/elements/CustomButton.vue';
import CustomCheckbox from '@/core/elements/CustomCheckbox.vue';
import { assertNotNullOrUndefined } from '@/utils/assertion.utils';
import { Product, SeatType } from '@/api/generated/api.enum';
import { useUsers } from '@/composables/schoolAdmin/useUsers';
import ComparePlans from '@/core/billing/ComparePlans.vue';
import BillingIntervalToggle from '@/core/billing/BillingIntervalToggle.vue';
import { useI18n } from 'vue-i18n';
import { canShowInvoicePaymentRequest, normalizeInvoicePaymentRequest } from './billingCheckoutFlow';
import { clampNumber } from 'common/utils/math.utils';

const emit = defineEmits<{
  continue: [
    value: {
      plan: Components.Schemas.ProductTypeDto;
      quantity?: number;
      billingInterval: BillingInterval;
      invoicePaymentRequested: boolean;
    },
  ];
  'plan-draft-change': [value: Components.Schemas.ProductTypeDto];
  'interval-draft-change': [value: BillingInterval];
  'quantity-draft-change': [value: { teamQuantity: number; studentQuantity: number }];
  'create-offer': [];
}>();

const props = withDefaults(
  defineProps<{
    active?: boolean;
    initialPlan?: Components.Schemas.ProductTypeDto;
    initialInterval?: BillingInterval;
    initialTeamQuantity?: number;
    initialStudentQuantity?: number;
    initialInvoicePaymentRequested?: boolean;
    isCheckoutFlow?: boolean;
    disabled?: boolean;
    checkoutSource?: Components.Schemas.CheckoutSource;
    yearlyOnly?: boolean;
    showCreateOfferLink?: boolean;
    blockMonthlyContinue?: boolean;
    monthlyContinueBlockedHint?: string;
  }>(),
  {
    active: true,
    initialPlan: undefined,
    initialInterval: 'year',
    initialTeamQuantity: MIN_TEAM_QUANTITY,
    initialStudentQuantity: MIN_STUDENT_QUANTITY,
    initialInvoicePaymentRequested: false,
    isCheckoutFlow: false,
    checkoutSource: undefined,
    yearlyOnly: false,
    showCreateOfferLink: false,
    blockMonthlyContinue: false,
    monthlyContinueBlockedHint: undefined,
  },
);

const { initialPlan } = toRefs(props);

const { billingStatus, canManageBilling } = useBillingState();
const { allBillingProductsWithMeta } = useBillingProducts(() => props.active !== false);
const { checkoutOptions } = useBillingCheckoutOptions(() => props.active !== false);
const { previewTenantPlanSwitch } = useBillingRequests(canManageBilling);
const { users } = useUsers();
const { t } = useI18n();
const team = ref<number>(props.initialTeamQuantity);
const students = ref<number>(props.initialStudentQuantity);
const selectedProduct = ref<ProductWithMeta | null>(null);
const billingInterval = ref<BillingInterval>(props.initialInterval);
const invoicePaymentRequested = ref<boolean>(props.initialInvoicePaymentRequested);

type CheckoutPricedProduct = ProductWithMeta & {
  checkoutPriceDisplay: CheckoutPriceDisplay;
};

const savingsPercent = computed(() => yearlySavingsPercent);

const availableProducts = computed<ProductWithMeta[]>(() =>
  allBillingProductsWithMeta.value
    .filter(isProductAvailableForPurchase)
    .sort((a, b) => purchasableProductOrder.indexOf(a.product) - purchasableProductOrder.indexOf(b.product)),
);

const visibleProducts = computed<ProductWithMeta[]>(() => {
  if (billingInterval.value === 'month') {
    return availableProducts.value.filter((p) => hasMonthlyBilling(p));
  }
  return availableProducts.value;
});

const initialProduct = computed<ProductWithMeta | null>(() => {
  if (!initialPlan.value) return null;
  return availableProducts.value.find((p) => p.id === initialPlan.value?.id) ?? null;
});

const isProductVisible = (product: ProductWithMeta | null | undefined): boolean => {
  if (!product) return false;
  return visibleProducts.value.some((p) => p.id === product.id);
};

const preferredProduct = computed<ProductWithMeta | null>(() => {
  if (isProductVisible(initialProduct.value)) return initialProduct.value;
  const schoolProduct = visibleProducts.value.find((p) => p.product === Product.school);
  if (schoolProduct) return schoolProduct;
  return visibleProducts.value.length > 0 ? visibleProducts.value[0] : null;
});

const discountPercentForProduct = (p: ProductWithMeta): number =>
  getDiscountPercentForCheckoutSource(props.checkoutSource, p, {
    interval: billingInterval.value,
    winbackOffer: checkoutOptions.value?.winbackOffer,
  });

const supportsMonthlyBilling = computed<boolean>(
  () => !props.yearlyOnly && availableProducts.value.some((p) => hasMonthlyBilling(p)),
);

const showInvoicePaymentRequest = computed<boolean>(() =>
  canShowInvoicePaymentRequest({
    billingInterval: billingInterval.value,
    invoiceEnabled: checkoutOptions.value?.invoiceEnabled,
    isCheckoutFlow: props.isCheckoutFlow,
  }),
);

const checkoutCurrency = computed<string>(() => getProductsMarketCurrency(allBillingProductsWithMeta.value));

const formatCheckoutTotal = (amountCents: number): string => {
  return formatAmount(amountCents, checkoutCurrency.value);
};

const formatComparableMonthlyTotal = (amountCents: number): string => {
  return formatCheckoutTotal(toMonthlyEquivalentCents(amountCents, billingInterval.value));
};

const checkoutPriceDetailLabel = (totalCents: number): string => {
  if (billingInterval.value === 'month') return t('billing.products.units.monthlyCancelable');

  return t('billing.products.units.forOneYear', {
    price: formatCheckoutTotal(totalCents),
  });
};

const pricedProducts = computed<CheckoutPricedProduct[]>(() =>
  visibleProducts.value.map((p) => {
    const discountPercent = discountPercentForProduct(p);
    const { baseCents, totalCents: finalCents } = computeProductPriceBreakdownCents(
      p,
      { team: team.value, students: students.value },
      { interval: billingInterval.value, discountPercent },
    );
    const originalPriceLabel = discountPercent > 0 ? formatCheckoutTotal(baseCents) : undefined;
    const checkoutOriginalPriceLabel = discountPercent > 0 ? formatComparableMonthlyTotal(baseCents) : undefined;

    return {
      ...p,
      priceLabel: formatCheckoutTotal(finalCents),
      originalPriceLabel,
      appliedDiscountPercent: discountPercent > 0 ? discountPercent : undefined,
      checkoutPriceDisplay: {
        priceLabel: formatComparableMonthlyTotal(finalCents),
        originalPriceLabel: checkoutOriginalPriceLabel,
        detailLabel: checkoutPriceDetailLabel(finalCents),
      },
    };
  }),
);

const minTeam = computed<number>(() => getMinTeamQuantity(users.value.length));

const minStudents = computed<number>(() => getMinStudentQuantity(checkoutOptions.value?.studentSeatsUsed ?? 0));

const quantity = computed<number>(() => {
  if (!selectedProduct.value) return 1;

  if (!selectedProduct.value.supportsSeats) return 1;

  if (selectedProduct.value.seatType === SeatType.user) {
    return clampNumber(team.value, minTeam.value, MAX_SEAT_QUANTITY);
  }

  return clampNumber(students.value, minStudents.value, MAX_SEAT_QUANTITY);
});

const setSelectedProduct = (plan: ProductWithMeta | null, emitDraft = false): void => {
  if (selectedProduct.value?.id === plan?.id) {
    if (emitDraft && plan) emit('plan-draft-change', plan);
    return;
  }
  selectedProduct.value = plan;
  if (emitDraft && plan) emit('plan-draft-change', plan);
};

const selectPlan = (plan: ProductWithMeta): void => {
  setSelectedProduct(plan, true);
};

// Detects when the current selection (product + interval + seat count) exactly
// matches the tenant's active subscription, so we can block a pointless submit
// that would otherwise hit Stripe with a no-op switch.
const isNoopSelection = computed<boolean>(() => {
  if (!selectedProduct.value) return false;
  const active = billingStatus.value?.products.find(
    (p) => p.productTypeId === selectedProduct.value?.id && p.isCurrentlyActive,
  );
  if (!active) return false;
  if (active.billingInterval !== billingInterval.value) return false;
  if (selectedProduct.value.supportsSeats && active.seats !== quantity.value) return false;
  return true;
});

// A noop selection may still carry a scheduled period-end change in Stripe; submitting then
// cancels that scheduled change instead of switching plans, so the button must stay enabled.
const noopReleasesScheduledChange = ref(false);

watch(
  [isNoopSelection, () => props.active],
  async ([isNoop, active]) => {
    noopReleasesScheduledChange.value = false;
    if (!active || !isNoop || !selectedProduct.value) return;

    try {
      const preview = await previewTenantPlanSwitch(selectedProduct.value.id, {
        quantity: quantity.value,
        billingInterval: billingInterval.value,
      });
      if (!isNoopSelection.value) return;
      noopReleasesScheduledChange.value = preview?.releasesScheduledChange === true;
    } catch {
      // Error toast is already shown by the composable; the submit simply stays disabled.
    }
  },
  { immediate: true },
);

const canCancelScheduledChange = computed<boolean>(() => isNoopSelection.value && noopReleasesScheduledChange.value);

const isMonthlyOfferBlocked = computed<boolean>(() => props.blockMonthlyContinue && billingInterval.value === 'month');

const submitHint = computed<string | null>(() => {
  if (canCancelScheduledChange.value) return t('billing.checkoutProductDialog.cancelScheduledChangeHint');
  if (isNoopSelection.value) return t('billing.checkoutProductDialog.currentSelectionHint');
  if (isMonthlyOfferBlocked.value) return props.monthlyContinueBlockedHint ?? null;
  return null;
});

const isSubmitDisabled = computed<boolean>(
  () =>
    !selectedProduct.value ||
    props.disabled ||
    (isNoopSelection.value && !canCancelScheduledChange.value) ||
    isMonthlyOfferBlocked.value,
);

const onSubmit = (): void => {
  assertNotNullOrUndefined(selectedProduct.value);
  if (isSubmitDisabled.value) return;
  emit('continue', {
    plan: selectedProduct.value,
    quantity: quantity.value,
    billingInterval: billingInterval.value,
    invoicePaymentRequested: normalizeInvoicePaymentRequest({
      billingInterval: billingInterval.value,
      invoiceEnabled: checkoutOptions.value?.invoiceEnabled,
      invoicePaymentRequested: invoicePaymentRequested.value,
      isCheckoutFlow: props.isCheckoutFlow,
    }),
  });
};

const emitQuantityDraftChange = (): void => {
  emit('quantity-draft-change', {
    teamQuantity: team.value,
    studentQuantity: students.value,
  });
};

watch(
  () => minTeam.value,
  (newMin) => {
    if (team.value < newMin) team.value = newMin;
  },
  { immediate: true },
);

watch(
  () => minStudents.value,
  (newMin) => {
    if (students.value < newMin) students.value = newMin;
  },
  { immediate: true },
);

watch(
  () => props.initialTeamQuantity,
  (newQuantity) => {
    team.value = constrainSeatQuantity({
      value: newQuantity,
      minValue: minTeam.value,
      step: TEAM_QUANTITY_STEP,
      ceilToStep: true,
    });
  },
  { immediate: true },
);

watch(
  () => props.initialStudentQuantity,
  (newQuantity) => {
    students.value = constrainSeatQuantity({
      value: newQuantity,
      minValue: minStudents.value,
      step: STUDENT_QUANTITY_STEP,
      ceilToStep: true,
    });
  },
  { immediate: true },
);

watch([team, students], emitQuantityDraftChange, { immediate: true });

watch(
  initialProduct,
  (newPlan) => {
    if (isProductVisible(newPlan)) setSelectedProduct(newPlan, true);
  },
  { immediate: true },
);

watch(
  visibleProducts,
  () => {
    if (isProductVisible(selectedProduct.value)) return;
    setSelectedProduct(preferredProduct.value, true);
  },
  { immediate: true },
);

watch(
  () => props.initialInterval,
  (newInterval) => {
    billingInterval.value = props.yearlyOnly ? 'year' : newInterval;
  },
);

watch(
  () => props.initialInvoicePaymentRequested,
  (newValue) => {
    invoicePaymentRequested.value = newValue;
  },
);

watch(showInvoicePaymentRequest, (canShow) => {
  if (!canShow) invoicePaymentRequested.value = false;
});

// When switching to monthly and selected product has no monthly price, deselect it
watch(billingInterval, (newInterval) => {
  emit('interval-draft-change', newInterval);
  if (newInterval === 'month') invoicePaymentRequested.value = false;
  if (newInterval === 'month' && selectedProduct.value && !hasMonthlyBilling(selectedProduct.value)) {
    setSelectedProduct(visibleProducts.value[0] ?? null, true);
  }
});

// When the selected section has no monthly option (e.g. school), force yearly
watch(
  supportsMonthlyBilling,
  (supportsMonthly) => {
    if (!supportsMonthly) billingInterval.value = 'year';
  },
  { immediate: true },
);

watch(
  () => props.yearlyOnly,
  (yearlyOnly) => {
    if (yearlyOnly) billingInterval.value = 'year';
  },
  { immediate: true },
);
</script>

<template>
  <form class="flex h-full min-h-0 flex-col overflow-hidden bg-surface-primary" @submit.prevent="onSubmit">
    <div class="min-h-0 flex-1 overflow-y-auto px-content pb-xl pt-0 max-md:px-lg max-md:pb-lg">
      <div class="flex flex-col gap-xl">
        <div class="grid grid-cols-2 gap-xl max-md:grid-cols-1 max-md:gap-md">
          <div class="flex min-w-0 flex-col gap-sm">
            <span class="text-body leading-[170%] text-primary">
              {{ $t('billing.checkoutProductDialog.teacherQuestion') }}
            </span>
            <NumberStepper
              v-model="team"
              :aria-label="$t('billing.checkoutProductDialog.teacherQuestion')"
              :min="minTeam"
              :max="MAX_SEAT_QUANTITY"
              :step="TEAM_QUANTITY_STEP"
              ceil-step
              variant="large"
            />
          </div>
          <div class="flex min-w-0 flex-col gap-sm">
            <span class="text-body leading-[170%] text-primary">
              {{ $t('billing.checkoutProductDialog.studentQuestion') }}
            </span>
            <NumberStepper
              v-model="students"
              :aria-label="$t('billing.checkoutProductDialog.studentQuestion')"
              :min="minStudents"
              :max="MAX_SEAT_QUANTITY"
              :step="STUDENT_QUANTITY_STEP"
              ceil-step
              variant="large"
            />
          </div>
        </div>

        <BillingIntervalToggle
          v-if="supportsMonthlyBilling"
          v-model="billingInterval"
          :savings-percent="savingsPercent"
        />

        <div
          class="grid grid-cols-2 items-start gap-xl max-md:grid-cols-1 max-md:gap-md"
          role="radiogroup"
          :aria-label="$t('billing.checkoutProductDialog.planSelectionAriaLabel')"
        >
          <BillingProductCheckoutCard
            v-for="product in pricedProducts"
            :key="product.id"
            selectable
            :product="product"
            :billing-interval="billingInterval"
            :checkout-price-display="product.checkoutPriceDisplay"
            :is-selected="selectedProduct?.id === product.id"
            class="cursor-pointer"
            @select="selectPlan(product)"
            @update:is-selected="() => selectPlan(product)"
          />
        </div>

        <CustomCheckbox
          v-if="showInvoicePaymentRequest"
          v-model="invoicePaymentRequested"
          align-items="center"
          class="!gap-smd text-body leading-[170%] text-primary"
        >
          <span class="min-w-0 flex-1">
            {{ $t('billing.invoicePayment.requestOptionText') }}
          </span>
        </CustomCheckbox>
      </div>
    </div>

    <div
      :class="[
        'shrink-0',
        'bg-surface-primary px-content pb-lg pt-md',
        'flex items-center justify-between gap-md',
        'max-md:flex-col max-md:items-stretch max-md:px-lg max-md:pb-mobile-safe-bottom-lg',
      ]"
    >
      <ComparePlans
        class="text-footer leading-[160%] tracking-[0.01em] max-md:justify-center"
        :show-offer-link="showCreateOfferLink"
        @create-offer="emit('create-offer')"
      />

      <div class="flex items-center justify-end gap-md max-md:flex-col max-md:items-stretch">
        <span v-if="submitHint" class="text-secondary text-footer">
          {{ submitHint }}
        </span>
        <CustomButton
          class="w-min justify-center whitespace-nowrap max-md:w-full"
          :disabled="isSubmitDisabled"
          type="submit"
        >
          {{ $t('general.continue') }}
        </CustomButton>
      </div>
    </div>
  </form>
</template>
